<template>
  <div class="benefit-type-edit w-100">
    <div class="w-100 px-8 py-4 bg-white border-bottom">
      <div class="d-flex justify-content-between align-items-center">
        <b-button variant="light" class="btn btn-secondary" @click="$router.push({ name: 'benefit_type' })">
          <i class="ki ki-arrow-left icon-sm"></i> Quay lại
        </b-button>
        <div>
          <b-button variant="secondary" class="mr-2" @click="$router.push({ name: 'benefit_type' })">Hủy</b-button>
          <loading-button :handler="handleSave" class="btn btn-success">
            {{ isEdit ? 'Cập nhật' : 'Tạo mới' }}
          </loading-button>
        </div>
      </div>
    </div>

    <b-overlay :show="loading">
      <div class="container-fluid py-6">
        <div class="row justify-content-center">
          <div class="col-lg-8">
            <b-card no-body class="shadow-sm">
              <div class="card-body p-6">

                <!-- Thông tin cơ bản -->
                <div class="form-section mb-6">
                  <h6 class="section-title">Thông tin cơ bản</h6>
                  <b-row>
                    <b-col cols="12">
                      <basic-input
                        :value.sync="form.title"
                        name="title"
                        label="Tiêu đề"
                        placeholder="VD: Ưu đãi khám sức khỏe tổng quát"
                        validate="required"
                        v-validate="'required'"
                        data-vv-as="Tiêu đề"
                      />
                      <span class="text-danger">{{ errors.first('title') }}</span>
                    </b-col>

                    <b-col cols="6">
                      <basic-select
                        label="Loại nội dung"
                        name="contentType"
                        :options="contentTypeOptions"
                        track-by="value"
                        value-label="text"
                        :value.sync="selectedContentType"
                        @input="form.contentType = $event"
                      />
                    </b-col>

                    <!-- Tag — dùng chung BundleTag với BundleService -->
                    <b-col cols="6">
                      <label>Tag</label>
                      <div ref="tagDropdownContainer" class="position-relative">
                        <input
                          v-model="tagSearchText"
                          type="text"
                          class="form-control"
                          placeholder="Tìm hoặc tạo tag..."
                          @input="onTagSearch"
                          @focus="showTagDropdown = true"
                        />
                        <div
                          v-if="showTagDropdown"
                          class="dropdown-menu show position-absolute w-100"
                          style="top: 100%; left: 0; z-index: 1000; max-height: 240px; overflow-y: auto;"
                        >
                          <div v-if="filteredTags.length > 0">
                            <a
                              v-for="tag in filteredTags"
                              :key="tag.value"
                              href="#"
                              class="dropdown-item"
                              @click.prevent="selectTag(tag)"
                            >{{ tag.text }}</a>
                          </div>
                          <div v-if="tagSearchText && filteredTags.length === 0">
                            <div class="dropdown-divider"></div>
                            <a href="#" class="dropdown-item text-success font-weight-bold" @click.prevent="createNewTag">
                              <i class="ki ki-plus"></i> Tạo tag mới: "{{ tagSearchText }}"
                            </a>
                          </div>
                          <div v-if="!tagSearchText && tagOptions.length === 0" class="dropdown-item text-muted">
                            Không có dữ liệu
                          </div>
                        </div>
                      </div>
                    </b-col>

                    <b-col cols="12">
                      <basic-text-area
                        :value.sync="form.description"
                        name="description"
                        label="Nội dung"
                        placeholder="Nhập nội dung text..."
                        :rows="4"
                      />
                    </b-col>

                    <b-col v-if="form.contentType === 3" cols="12">
                      <basic-input
                        :value.sync="videoUrl"
                        name="videoUrl"
                        label="URL video"
                        placeholder="https://..."
                      />
                    </b-col>

                    <b-col cols="6">
                      <basic-input
                        :value.sync="form.order"
                        name="order"
                        label="Thứ tự hiển thị"
                        type="number"
                        placeholder="0"
                      />
                    </b-col>
                  </b-row>
                </div>

                <!-- Hình ảnh gallery — chỉ hiện khi loại nội dung = Hình ảnh -->
                <div v-if="form.contentType === 2" class="form-section mb-6">
                  <h6 class="section-title">Hình ảnh</h6>
                  <div class="gallery-upload-area mb-3">
                    <label class="btn btn-light-success btn-sm">
                      <i class="la la-plus"></i> Thêm hình ảnh
                      <input
                        type="file"
                        accept="image/png,image/jpeg,image/jpg"
                        multiple
                        class="d-none"
                        @change="onGalleryFilesSelected"
                      />
                    </label>
                    <small class="text-muted ml-3">Hình đầu tiên trong danh sách sẽ là ảnh đại diện (index 1)</small>
                  </div>

                  <div v-if="galleryImages.length === 0" class="text-muted py-3">Chưa có hình ảnh nào</div>

                  <div v-else class="gallery-list">
                    <div
                      v-for="(img, index) in galleryImages"
                      :key="img._key"
                      class="gallery-item d-flex align-items-center mb-2 p-2 border rounded"
                    >
                      <div class="gallery-thumb mr-3">
                        <img :src="img.previewUrl" alt="" style="width:60px;height:60px;object-fit:cover;border-radius:4px;" />
                      </div>
                      <div class="flex-grow-1 text-muted small">
                        Ảnh {{ index + 1 }}
                        <span v-if="img.uploading" class="ml-2 text-warning"><i class="la la-spinner la-spin"></i> Đang upload...</span>
                        <span v-else-if="img.error" class="ml-2 text-danger">Lỗi upload</span>
                      </div>
                      <div class="d-flex align-items-center">
                        <b-button size="sm" variant="light" :disabled="index === 0" class="mr-1 px-2" @click="moveGalleryUp(index)">
                          <i class="la la-arrow-up"></i>
                        </b-button>
                        <b-button size="sm" variant="light" :disabled="index === galleryImages.length - 1" class="mr-1 px-2" @click="moveGalleryDown(index)">
                          <i class="la la-arrow-down"></i>
                        </b-button>
                        <b-button size="sm" variant="light-danger" class="px-2" @click="removeGalleryImage(index)">
                          <i class="la la-trash"></i>
                        </b-button>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Cơ sở áp dụng / Giờ mở cửa -->
                <div class="form-section mb-6">
                  <b-row>
                    <b-col cols="12">
                      <basic-input :value.sync="form.location" name="location" label="Cơ sở áp dụng" placeholder="VD: Tất cả chi nhánh DiaB" />
                    </b-col>
                    <b-col cols="12">
                      <basic-text-area
                        :value.sync="form.openTime"
                        name="openTime"
                        label="Giờ mở cửa / áp dụng"
                        placeholder="VD: Thứ 2 - Thứ 6: 8:00 - 17:00"
                        :rows="3"
                      />
                    </b-col>
                  </b-row>
                </div>

                <!-- Voucher -->
                <div class="form-section mb-4">
                  <h6 class="section-title">Voucher</h6>
                  <b-row>
                    <b-col cols="12" class="mb-4">
                      <b-form-radio-group
                        v-model="form.hasVoucher"
                        :options="[{ text: 'Không có voucher', value: 0 }, { text: 'Có voucher', value: 1 }]"
                        buttons
                        button-variant="outline-success"
                        size="sm"
                      />
                    </b-col>

                    <!-- Ảnh ưu đãi — chỉ hiện khi không có voucher -->
                    <b-col v-if="form.hasVoucher !== 1" cols="12" class="mb-4">
                      <label class="font-weight-bold d-block mb-2">Ảnh ưu đãi</label>
                      <div class="d-flex align-items-center">
                        <label class="btn btn-light-success btn-sm mr-3 mb-0">
                          <i class="la la-image"></i> Chọn ảnh
                          <input type="file" accept="image/png,image/jpeg,image/jpg" class="d-none" @change="onBenefitImageSelected" />
                        </label>
                        <span v-if="benefitImage.uploading" class="text-warning small"><i class="la la-spinner la-spin"></i> Đang upload...</span>
                        <span v-else-if="benefitImage.error" class="text-danger small">Lỗi upload</span>
                      </div>
                      <div v-if="benefitImage.previewUrl" class="mt-2 d-flex align-items-center">
                        <img :src="benefitImage.previewUrl" alt="benefit" style="height:80px;object-fit:cover;border-radius:4px;border:1px solid #eee;" />
                        <b-button size="sm" variant="light-danger" class="ml-2" @click="removeBenefitImage">
                          <i class="la la-trash"></i>
                        </b-button>
                      </div>
                    </b-col>

                    <template v-if="form.hasVoucher === 1">
                      <b-col cols="6">
                        <basic-input :value.sync="form.voucherName" name="voucherName" label="Tên voucher" placeholder="VD: Voucher khám tổng quát" />
                      </b-col>
                      <b-col cols="6">
                        <basic-input :value.sync="form.voucherSubName" name="voucherSubName" label="Subtitle" placeholder="VD: Áp dụng tại tất cả chi nhánh" />
                      </b-col>
                      <b-col cols="6">
                        <basic-input :value.sync="form.voucherCode" name="voucherCode" label="Mã voucher" placeholder="VD: HEALTH2025" />
                      </b-col>
                      <b-col cols="6">
                        <basic-input :value.sync="form.voucherValue" name="voucherValue" label="Giá trị (VNĐ)" placeholder="VD: 200000" />
                      </b-col>
                      <b-col cols="6">
                        <basic-input :value.sync="form.applicableTo" name="applicableTo" label="Áp dụng cho" placeholder="VD: Khách hàng mới" />
                      </b-col>
                      <b-col cols="6">
                        <BasicDatePicker :value.sync="form.validUntil" name="validUntil" label="Hiệu lực đến" placeholder="Chọn ngày..." />
                      </b-col>
                      <b-col cols="12">
                        <basic-input :value.sync="form.applicableLocation" name="applicableLocation" label="Áp dụng tại" placeholder="VD: Tất cả chi nhánh DiaB" />
                      </b-col>

                      <!-- Danh sách mã voucher (pool) — chỉ khi đã tạo ưu đãi -->
                      <b-col cols="12" class="mt-4">
                        <div v-if="!isEdit" class="text-muted small">
                          Lưu ưu đãi trước, sau đó quay lại đây để nhập danh sách mã.
                        </div>
                        <template v-else>
                          <div class="d-flex justify-content-between align-items-center mb-2">
                            <label class="font-weight-bold mb-0">
                              Danh sách mã voucher ({{ voucherCodes.length }})
                              <small class="text-muted d-block font-weight-normal">
                                Còn trống: {{ voucherCodesAvailableCount }} — Đã gán: {{ voucherCodes.length - voucherCodesAvailableCount }}
                              </small>
                            </label>
                            <label class="btn btn-light-success btn-sm mb-0" :class="{ disabled: importingCodes }">
                              <i class="la la-file-excel-o"></i> Import từ Excel
                              <input
                                type="file"
                                accept=".xlsx,.xls"
                                class="d-none"
                                :disabled="importingCodes"
                                @change="onImportExcelSelected"
                              />
                            </label>
                          </div>

                          <div v-if="importingCodes" class="text-warning small mb-2">
                            <i class="la la-spinner la-spin"></i> Đang import...
                          </div>

                          <div v-if="voucherCodesLoading" class="text-muted small">Đang tải danh sách mã...</div>
                          <div v-else-if="voucherCodes.length === 0" class="text-muted small">Chưa có mã nào trong danh sách</div>
                          <div v-else class="voucher-code-list border rounded" style="max-height: 260px; overflow-y: auto;">
                            <table class="table table-sm mb-0">
                              <thead>
                                <tr>
                                  <th>Mã</th>
                                  <th>Trạng thái</th>
                                  <th class="text-right">Thao tác</th>
                                </tr>
                              </thead>
                              <tbody>
                                <tr v-for="code in voucherCodes" :key="code.id">
                                  <td>{{ code.code }}</td>
                                  <td>
                                    <span v-if="code.assignedAccountHisId" class="text-muted">Đã gán</span>
                                    <span v-else class="text-success">Còn trống</span>
                                  </td>
                                  <td class="text-right">
                                    <b-button
                                      v-if="!code.assignedAccountHisId"
                                      size="sm"
                                      variant="light-danger"
                                      class="px-2"
                                      @click="deleteVoucherCode(code)"
                                    >
                                      <i class="la la-trash"></i>
                                    </b-button>
                                  </td>
                                </tr>
                              </tbody>
                            </table>
                          </div>
                        </template>
                      </b-col>
                    </template>
                  </b-row>
                </div>

              </div>
            </b-card>
          </div>
        </div>
      </div>
    </b-overlay>
  </div>
</template>

<script>
export default {
  name: 'BenefitTypeEdit',
  props: {
    id: { type: String, default: null },
  },
  data() {
    return {
      loading: false,
      form: {
        title: '',
        contentType: 1,
        description: '',
        status: 1,
        hasVoucher: 0,
        voucherName: '',
        voucherSubName: '',
        voucherCode: '',
        voucherValue: '',
        applicableTo: '',
        applicableLocation: '',
        validUntil: null,
        location: '',
        openTime: '',
        order: 0,
        bundleTagId: null,
      },
      selectedContentType: { value: 1, text: 'Text' },
      contentTypeOptions: [
        { value: 1, text: 'Text' },
        { value: 2, text: 'Hình ảnh' },
        { value: 3, text: 'Video' },
      ],
      tagOptions: [],
      filteredTags: [],
      tagSearchText: '',
      showTagDropdown: false,
      galleryImages: [],
      benefitImage: { imageId: null, previewUrl: null, uploading: false, error: false },
      videoUrl: '',
      keyCounter: 0,
      voucherCodes: [],
      voucherCodesLoading: false,
      importingCodes: false,
    };
  },

  computed: {
    isEdit() {
      return !!this.$route.params.id;
    },
    voucherCodesAvailableCount() {
      return this.voucherCodes.filter((c) => !c.assignedAccountHisId).length;
    },
  },

  created() {
    this.loadTags();
    if (this.isEdit) {
      this.loadDetail();
      this.loadVoucherCodes();
    }
  },

  mounted() {
    document.addEventListener('click', this.onDocumentClick);
  },

  beforeDestroy() {
    document.removeEventListener('click', this.onDocumentClick);
  },

  methods: {
    async loadDetail() {
      this.loading = true;
      try {
        const { data } = await this.$api.get(`/BundleBenefitType/${this.$route.params.id}`);
        this.form = { ...data };
        if (data.validUntil) {
          this.form.validUntil = typeof data.validUntil === 'number'
            ? new Date(data.validUntil * 1000).toISOString().split('T')[0]
            : String(data.validUntil).split('T')[0];
        }
        this.selectedContentType = this.contentTypeOptions.find((o) => o.value === data.contentType) || this.contentTypeOptions[0];
        if (data.bundleTagId) {
          await this._restoreTag(data.bundleTagId);
        }
        this._restoreMedia(data.media || []);
      } catch (e) {
        this.$toastr.e('Không thể tải thông tin ưu đãi');
      } finally {
        this.loading = false;
      }
    },

    async _restoreTag(bundleTagId) {
      if (!this.tagOptions.length) await this.loadTags();
      const found = this.tagOptions.find((t) => String(t.value) === String(bundleTagId));
      if (found) {
        this.tagSearchText = found.text;
      }
    },

    async loadTags() {
      try {
        const { data } = await this.$api.get('/BundleTag/All');
        this.tagOptions = (data || []).map((item) => ({ value: item.id, text: item.name }));
        this.filteredTags = [...this.tagOptions];
      } catch (e) {
        console.error('Không thể tải danh sách tag:', e);
      }
    },

    onTagSearch() {
      this.filteredTags = this.tagSearchText
        ? this.tagOptions.filter((t) => t.text.toLowerCase().includes(this.tagSearchText.toLowerCase()))
        : [...this.tagOptions];
      if (!this.tagSearchText) {
        this.form.bundleTagId = null;
      }
    },

    selectTag(tag) {
      this.form.bundleTagId = tag.value;
      this.tagSearchText = tag.text;
      this.showTagDropdown = false;
    },

    async createNewTag() {
      const tagName = this.tagSearchText.trim();
      if (!tagName) return;
      try {
        const { data } = await this.$api.post('/BundleTag', { name: tagName });
        await this.loadTags();
        const newTag = (data?.id ? this.tagOptions.find((t) => String(t.value) === String(data.id)) : null)
          || this.tagOptions.find((t) => t.text === tagName);
        if (newTag) this.selectTag(newTag);
        this.$toastr.s('Tạo tag thành công');
      } catch {
        this.$toastr.e('Không thể tạo tag');
      }
    },

    onDocumentClick(event) {
      if (this.$refs.tagDropdownContainer && !this.$refs.tagDropdownContainer.contains(event.target)) {
        this.showTagDropdown = false;
      }
    },

    _restoreMedia(items) {
      this.galleryImages = items
        .filter((m) => m.type === 1)
        .sort((a, b) => a.sortOrder - b.sortOrder)
        .map((m) => ({
          _key: ++this.keyCounter,
          imageId: m.imageId,
          previewUrl: this._imageUrl(m.imageId),
          uploading: false,
          error: false,
        }));

      const benefit = items.find((m) => m.type === 4);
      if (benefit) {
        this.benefitImage = {
          imageId: benefit.imageId,
          previewUrl: this._imageUrl(benefit.imageId),
          uploading: false,
          error: false,
        };
      }

      const video = items.find((m) => m.type === 2);
      if (video) {
        this.videoUrl = video.url || '';
      } else if (this.form.contentType === 3 && this.form.description) {
        // Data cũ trước khi có fix này lưu URL video vào description — fallback hiển thị lại
        this.videoUrl = this.form.description;
      }
    },

    _imageUrl(imageId) {
      if (!imageId) return null;
      return `${process.env.VUE_APP_API}/Image/${imageId}`;
    },

    async onGalleryFilesSelected(e) {
      const files = Array.from(e.target.files || []);
      e.target.value = '';
      for (const file of files) {
        const key = ++this.keyCounter;
        const previewUrl = URL.createObjectURL(file);
        const entry = { _key: key, imageId: null, previewUrl, uploading: true, error: false };
        this.galleryImages.push(entry);
        try {
          const imageId = await this._uploadFile(file);
          entry.imageId = imageId;
          entry.uploading = false;
        } catch {
          entry.uploading = false;
          entry.error = true;
        }
      }
    },

    async onBenefitImageSelected(e) {
      const file = e.target.files[0];
      e.target.value = '';
      if (!file) return;
      this.benefitImage = { imageId: null, previewUrl: URL.createObjectURL(file), uploading: true, error: false };
      try {
        const imageId = await this._uploadFile(file);
        this.benefitImage.imageId = imageId;
        this.benefitImage.uploading = false;
      } catch {
        this.benefitImage.uploading = false;
        this.benefitImage.error = true;
      }
    },

    removeBenefitImage() {
      this.benefitImage = { imageId: null, previewUrl: null, uploading: false, error: false };
    },

    async _uploadFile(file) {
      const fd = new FormData();
      fd.append('images', file);
      fd.append('Type', 27);
      const { data } = await this.$api.post('/Image/Upload', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return data.id;
    },

    moveGalleryUp(index) {
      if (index === 0) return;
      const arr = [...this.galleryImages];
      [arr[index - 1], arr[index]] = [arr[index], arr[index - 1]];
      this.galleryImages = arr;
    },

    moveGalleryDown(index) {
      if (index === this.galleryImages.length - 1) return;
      const arr = [...this.galleryImages];
      [arr[index + 1], arr[index]] = [arr[index], arr[index + 1]];
      this.galleryImages = arr;
    },

    removeGalleryImage(index) {
      this.galleryImages.splice(index, 1);
    },

    async handleSave() {
      const valid = await this.$validator.validateAll();
      if (!valid) return;

      const pendingUploads = this.galleryImages.some((img) => img.uploading) || this.benefitImage.uploading;
      if (pendingUploads) {
        this.$toastr.e('Vui lòng chờ upload hình ảnh hoàn tất');
        return;
      }

      try {
        const payload = {
          ...this.form,
          description: this.form.contentType === 3 ? '' : this.form.description,
          validUntil: this.form.validUntil
            ? Math.floor(new Date(this.form.validUntil).getTime() / 1000)
            : null,
        };
        let benefitTypeId;
        if (this.isEdit) {
          await this.$api.put(`/BundleBenefitType/${this.$route.params.id}`, payload);
          benefitTypeId = this.$route.params.id;
          this.$toastr.s('Đã cập nhật ưu đãi');
        } else {
          const { data } = await this.$api.post('/BundleBenefitType', payload);
          benefitTypeId = data.id;
          this.$toastr.s('Đã tạo ưu đãi mới');
        }

        await this._saveMedia(benefitTypeId);
        this.$router.push({ name: 'benefit_type' });
      } catch (e) {
        this.$toastr.e('Không thể lưu ưu đãi');
      }
    },

    async _saveMedia(benefitTypeId) {
      const items = [];

      this.galleryImages.forEach((img, index) => {
        if (img.imageId) {
          items.push({ imageId: img.imageId, type: 1, sortOrder: index + 1 });
        }
      });

      if (this.benefitImage.imageId) {
        items.push({ imageId: this.benefitImage.imageId, type: 4, sortOrder: 0 });
      }

      if (this.form.contentType === 3 && this.videoUrl) {
        items.push({ url: this.videoUrl, type: 2, sortOrder: 0 });
      }

      await this.$api.post(`/BundleBenefitTypeMedia/save/${benefitTypeId}`, items);
    },

    async loadVoucherCodes() {
      this.voucherCodesLoading = true;
      try {
        const { data } = await this.$api.get(`/BundleBenefitVoucherList/benefit-type/${this.$route.params.id}`);
        this.voucherCodes = data || [];
      } catch (e) {
        this.$toastr.e('Không thể tải danh sách mã voucher');
      } finally {
        this.voucherCodesLoading = false;
      }
    },

    async onImportExcelSelected(e) {
      const file = e.target.files[0];
      e.target.value = '';
      if (!file) return;

      const fd = new FormData();
      fd.append('importFile', file);
      this.importingCodes = true;
      try {
        const { data } = await this.$api.post(
          `/BundleBenefitVoucherList/benefit-type/${this.$route.params.id}/import-excel`,
          fd,
          { headers: { 'Content-Type': 'multipart/form-data' } }
        );
        this.$toastr.s(`Đã thêm ${data.added} mã mới / tổng ${data.total} dòng`);
        if (data.duplicated > 0) {
          const preview = (data.duplicatedCodes || []).slice(0, 20).join(', ');
          const more = data.duplicatedCodes.length > 20 ? `, ... (+${data.duplicatedCodes.length - 20} mã nữa)` : '';
          this.$toastr.w(`Bỏ qua ${data.duplicated} mã trùng: ${preview}${more}`);
        }
        await this.loadVoucherCodes();
      } catch (e) {
        this.$toastr.e('Import file thất bại');
      } finally {
        this.importingCodes = false;
      }
    },

    async deleteVoucherCode(code) {
      if (!confirm(`Xóa mã "${code.code}"?`)) return;
      try {
        await this.$api.delete(`/BundleBenefitVoucherList/${code.id}`);
        this.voucherCodes = this.voucherCodes.filter((c) => c.id !== code.id);
        this.$toastr.s('Đã xóa mã');
      } catch (e) {
        this.$toastr.e('Không thể xóa mã (có thể đã được gán cho khách hàng)');
      }
    },
  },
};
</script>

<style scoped>
.form-section {
  border-bottom: 1px solid #f0f0f0;
  padding-bottom: 1.5rem;
}
.section-title {
  font-weight: 600;
  color: #3f4254;
  margin-bottom: 1rem;
  font-size: 0.95rem;
}
.gallery-item:hover {
  background-color: #f9f9f9;
}
</style>
